import { useId } from 'react'
import { Input, Label, Textarea } from '@teach/ui'
import { useAppSelector } from '@/store/hooks'
import type { ContentLang } from '@/features/editor/editorSlice'

interface Props {
  value: { ru: string | null; en: string | null }
  onChange: (next: { ru: string | null; en: string | null }) => void
  onBlur?: () => void
  label: string
  placeholder?: string
  as?: 'input' | 'textarea'
  maxLength?: number
  className?: string
  disabled?: boolean
  title?: string
  /** Какой слот правится. Без него — язык правки редактора (`editor.contentLang`);
   *  настройки курса передают свой, местный язык полей. */
  lang?: ContentLang
}

export const BilingualField = ({
  value,
  onChange,
  onBlur,
  label,
  placeholder,
  as = 'input',
  maxLength,
  className,
  disabled,
  title,
  lang: langProp,
}: Props) => {
  const id = useId()
  const storeLang = useAppSelector((s) => s.editor.contentLang)
  const lang = langProp ?? storeLang
  const current = lang === 'ru' ? (value.ru ?? '') : (value.en ?? '')

  // Находка 1: строка, состоящая ЦЕЛИКОМ из пробелов, нормализуется в '' при
  // записи. Нормализация переживает снятие подсказки «не заполнен» (п.14) и
  // нужна сама по себе: незаполненность второго слота теперь показывает точка
  // переключателя языка, а она считает ту же пустоту через `hasContentFor` —
  // слот из одних пробелов обязан доехать до PUT пустым, иначе точка гаснет, а
  // перевод молча не заказывается. Осмысленный пробел внутри/на конце текста не
  // трогаем — тримим только когда после trim() не осталось ничего.
  const handle = (v: string) => {
    const normalized = v.trim() === '' ? '' : v
    onChange(lang === 'ru' ? { ru: normalized, en: value.en } : { ru: value.ru, en: normalized })
  }

  return (
    <div className={className}>
      {/* Кода языка в подписи нет (п.14): какой слот правится, видно по
          селектору языка над формой. */}
      <Label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </Label>
      <div className="relative">
        {as === 'textarea' ? (
          <Textarea
            id={id}
            value={current}
            onChange={(e) => handle(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
            title={title}
            className={maxLength ? 'pb-7' : undefined}
          />
        ) : (
          <Input
            id={id}
            value={current}
            onChange={(e) => handle(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
            title={title}
            className={maxLength ? 'pr-20' : undefined}
          />
        )}
        {/* Счётчик «195/200» внутри поля, справа — только у полей с лимитом. Набрать
            больше нельзя: `maxLength` обрезает ввод, ошибки при сохранении не будет. */}
        {maxLength ? (
          <span
            className={`pointer-events-none absolute right-3 text-xs tabular-nums ${
              as === 'textarea' ? 'bottom-2' : 'top-1/2 -translate-y-1/2'
            } ${maxLength - current.length <= 20 ? 'text-warning' : 'text-muted-foreground'}`}
          >
            {`${current.length}/${maxLength}`}
          </span>
        ) : null}
      </div>
    </div>
  )
}
